import { Component, ElementRef, OnInit, ViewChild, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { WorkshopsService } from '../../services/workshops/workshops.service';
import { ProvidersService } from '../../services/providers/providers.service';
import { WorkshopModel } from '../../models/workshop-model/workshop-model';
import { ProviderModel } from '../../models/provider-model/provider-model';
import { CurrencyFormatter } from '../../utils/currency-formatter';
import { ToastService } from '../../services/toast/toast.service';
import { collectLowSaleRatioViolations, getSaleValueValidationError } from '../../utils/spare-part-validation.util';
import { SPARE_PART_DAMAGED_OPTIONS, SPARE_PART_DAMAGED_NO } from '../../constants/spare-part-damaged-states';
import { normalizeIsDamaged } from '../../utils/spare-part-packaging.util';
import { defaultFantUrgency, FantUrgency, normalizeFantUrgency } from '../../utils/spare-part-payload.util';
@Component({
  selector: 'app-order-form',
  templateUrl: './order-form.component.html',
  styleUrls: ['./order-form.component.css']
})
export class OrderFormComponent implements OnInit {
  sparePartsForm: FormGroup;
  employeeNames = environment.employeeNames;
  damagedStateOptions = SPARE_PART_DAMAGED_OPTIONS;
  workshops: WorkshopModel[] = [];
  providers: ProviderModel[] = [];

  // Modal para crear nuevo taller
  showCreateWorkshopModal = false;
  showWorkshopInfoModal = false;
  showEditWorkshopModal = false;
  showWorkshopDropdown = false;
  selectedWorkshopInfo: WorkshopModel | null = null;
  creatingWorkshop = false;
  updatingWorkshop = false;
  workshopEditScope: 'order-only' | 'global' = 'order-only';
  createWorkshopForm: FormGroup;
  editWorkshopForm: FormGroup;

  // Modal para gestionar proveedores
  showManageProvidersModal = false;
  creatingProvider = false;
  deletingProviderId: number | null = null;
  manageProviderForm: FormGroup;

  @ViewChild('daysUntilArrive') daysUntilArrival: ElementRef | undefined;

  form: FormGroup;
  arrivalDays = 0;
  orderSending = false;
  showLowSaleRatioConfirmModal = false;
  lowSaleRatioViolationLabels: string[] = [];
  private pendingOrderPayload: any | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private authService: AuthService,
    private workshopsService: WorkshopsService,
    private providersService: ProvidersService,
    private toastService: ToastService
  ) {
    this.sparePartsForm = this.fb.group({
      orderDate: [this.todayLocalDateString()],
      providerName: [''],  // OBLIGATORIO
      providerPhone: [''],  // OPCIONAL
      clientName: [''],  // OBLIGATORIO
      clientPhone: [''],  // OPCIONAL
      invoiceNumber: [''],  // OPCIONAL
      employeeName: [''],  // OBLIGATORIO
      stateOrder: [''],  // OBLIGATORIO
      orderNumber: [''],  // OBLIGATORIO
      siniestro: [''],  // OBLIGATORIO
      deliveryAddress: ['', Validators.required],  // OBLIGATORIO (validar "Indefinido")
      carMakeModel: [''],  // OPCIONAL
      isExport: [''],  // OBLIGATORIO (validar "Indefinido")
      sendYesOrNo: [''],  // OBLIGATORIO (validar "Indefinido")
      clienteUY: [''],  // OBLIGATORIO
      workshopId: [''],  // OPCIONAL - Taller asignado
      workshopName: [''],
      workshopAddress: [''],
      workshopPhone: [''],
      workshopCity: [''],
      workshopDepartment: [''],
      emergentComment: [''],
      spareParts: this.fb.array([])  // Array para los repuestos
    });
    this.form = this.fb.group({
      sparePartExpectedArrivalDate: [''], // Campo del formulario para la fecha de llegada
    });

    // Inicializar formulario para crear nuevo taller
    this.createWorkshopForm = this.fb.group({
      name: ['', Validators.required],
      address: [''],
      phoneNumber: [''],
      city: ['', Validators.required],
      department: [''],
      company: ['AUTOCOMERCIAL']
    });

    this.editWorkshopForm = this.fb.group({
      name: ['', Validators.required],
      address: [''],
      phoneNumber: [''],
      city: [''],
      department: [''],
      company: ['AUTOCOMERCIAL']
    });

    this.manageProviderForm = this.fb.group({
      name: ['', Validators.required]
    });
  }

  private todayLocalDateString(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn() == false) {
      location.href = '/login';
    }

    this.loadWorkshops();
    this.loadProviders();
    this.addSparePart();
    this.sparePartsForm.get('providerName')?.valueChanges.subscribe((providerName: string) => {
      this.applyProviderToSpareParts(providerName);
    });

    setTimeout(() => {
      this.sparePartsForm.updateValueAndValidity();
    }, 0);
  }

  loadWorkshops(): void {
    this.workshopsService.getWorkshops().subscribe({
      next: (workshops) => {
        if (!workshops || workshops.length === 0) {
          this.workshops = [];
          this.toastService.showInfo('No se encontraron talleres disponibles. Puedes crear uno nuevo.');
          return;
        }

        // El backend ya filtra los talleres eliminados en WorkshopService.GetWorkshops()
        // Por lo tanto, usamos directamente todos los talleres recibidos
        this.workshops = workshops || [];
      },
      error: (error) => {
        console.error('❌ Error al cargar talleres:', error);
        console.error('❌ Error details:', {
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          error: error?.error,
          url: error?.url
        });
        this.workshops = [];
        this.toastService.showError('Error al cargar los talleres. Verifica la conexión con el servidor.');
      }
    });
  }

  loadProviders(): void {
    this.providersService.getProviders().subscribe({
      next: (providers) => {
        this.providers = (providers || []).sort((a, b) => a.name.localeCompare(b.name));
      },
      error: (error) => {
        console.error('❌ Error al cargar proveedores:', error);
        this.providers = [];
        this.toastService.showError('Error al cargar proveedores. Puedes escribir manualmente o intentar de nuevo.');
      }
    });
  }

  // Función para normalizar valores numéricos usando la nueva utilidad
  normalizeNumericValue(value: any): number {
    return CurrencyFormatter.formatForBackend(value);
  }

  // Método para trackBy en el ngFor de talleres
  trackByWorkshopId(index: number, workshop: WorkshopModel): number {
    return workshop.workshopId || 0;
  }

  private isMarked(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  getSparePartHighlightClass(sparePartControl: any): string {
    return '';
  }

  // Método para normalizar inputs de texto en la UI usando la nueva utilidad
  handleNumericInput(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleInput(inputElement, { maxDecimals: 2, allowNegative: false });
  }

  // Método para formatear al perder el foco
  handleNumericBlur(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleBlur(inputElement, { maxDecimals: 2, minDecimals: 2, allowNegative: false });
  }

  // Método para preparar el campo al recibir foco
  handleNumericFocus(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleFocus(inputElement);
  }

  get spareParts(): FormArray {
    return this.sparePartsForm.get('spareParts') as FormArray;
  }

  onSparePartFantChanged(index: number, checked: boolean): void {
    const group = this.spareParts.at(index);
    group.get('fantUrgency')?.setValue(defaultFantUrgency(checked, null));
  }

  setSparePartFantUrgency(index: number, urgency: FantUrgency): void {
    const group = this.spareParts.at(index);
    if (!group.get('isFant')?.value) return;
    const current = normalizeFantUrgency(group.get('fantUrgency')?.value);
    group.get('fantUrgency')?.setValue(current === urgency ? null : urgency);
  }

  addSparePart(): void {
    this.spareParts.push(this.fb.group({
      sparePartName: [''],  // OBLIGATORIO
      sparePartNameEs: [''],  // OBLIGATORIO - Nombre en español para etiquetas
      sparePartNamePt: [''],  // OBLIGATORIO - Nombre en portugués para Excel
      unitPrice: [''],  // OPCIONAL
      amount: [''],  // OBLIGATORIO
      totalTax: [''],  // OPCIONAL
      taxNote: [''],  // OPCIONAL
      ncm: [''],  // OPCIONAL
      productCode: [''],  // OBLIGATORIO
      providerName: [this.sparePartsForm.get('providerName')?.value || ''],
      productState: [''],  // OBLIGATORIO
      comments: [''],  // OPCIONAL
      emergentComment: [''],
      shippingReceipt: [''],  // OPCIONAL
      isFant: [false],  // OPCIONAL
      fantUrgency: [null],
      recompra: [false],  // OPCIONAL
      control: [false],  // OPCIONAL
      isDamaged: [SPARE_PART_DAMAGED_NO],
      needReview: [false],
      codBr: [''],  // OPCIONAL
      valorDeVenta: [''],
      arrivalDays: [''],  // OPCIONAL
      netWheight: [''],  // OPCIONAL
      grossWheight: [''],  // OPCIONAL
      country: [''],  // OPCIONAL
      expectedArrivalDate: [''],  // OPCIONAL
    }));
  }

  private applyProviderToSpareParts(providerName: string): void {
    const normalizedProvider = (providerName || '').trim();
    this.spareParts.controls.forEach((control) => {
      control.get('providerName')?.setValue(normalizedProvider, { emitEvent: false });
    });
  }


  removeSparePart(index: number): void {
    this.spareParts.removeAt(index);
  }

  onSubmit(): void {
    this.orderSending = true;

    const orderValid = this.validateOrder();
    const sparePartsValid = this.validateSpareParts();
    const formValid = this.sparePartsForm.valid;

    if (orderValid && sparePartsValid && formValid) {
      // Preparar los datos para enviar al backend con procesamiento centralizado
      const formData = this.sparePartsForm.value;
      const processedData = this.processFormDataForBackend(formData);
      const lowSaleRatioViolations = this.collectLowSaleRatioViolationsFromForm();
      if (lowSaleRatioViolations.length > 0) {
        this.lowSaleRatioViolationLabels = lowSaleRatioViolations.map(v => v.label);
        this.pendingOrderPayload = processedData;
        this.showLowSaleRatioConfirmModal = true;
        this.orderSending = false;
        return;
      }

      this.submitOrderPayload(processedData);
    } else {
      // Mostrar qué validación está fallando
      let errorMessage = 'Error de validación: ';
      if (!orderValid) errorMessage += 'Orden inválida. ';
      if (!sparePartsValid) errorMessage += 'Repuestos inválidos. ';
      if (!formValid) errorMessage += 'Formulario inválido. ';

      console.error('Validation failed:', { orderValid, sparePartsValid, formValid });
      this.toastService.showWarning(errorMessage);
      this.orderSending = false;
    }
  }

  confirmLowSaleRatioAndSubmit(): void {
    if (!this.pendingOrderPayload) {
      this.showLowSaleRatioConfirmModal = false;
      return;
    }

    const payload = this.pendingOrderPayload;
    this.showLowSaleRatioConfirmModal = false;
    this.pendingOrderPayload = null;
    this.orderSending = true;
    this.submitOrderPayload(payload);
  }

  cancelLowSaleRatioConfirmation(): void {
    this.showLowSaleRatioConfirmModal = false;
    this.pendingOrderPayload = null;
    this.lowSaleRatioViolationLabels = [];
    this.orderSending = false;
  }

  private collectLowSaleRatioViolationsFromForm() {
    const orderNumber = this.sparePartsForm.get('orderNumber')?.value || '';
    const siniestro = this.sparePartsForm.get('siniestro')?.value || '';
    const candidates = this.spareParts.controls.map((sparePart) => ({
      sparePartName: sparePart.get('sparePartName')?.value,
      productCode: sparePart.get('productCode')?.value,
      orderNumber,
      siniestro,
      unitPrice: sparePart.get('unitPrice')?.value,
      valorDeVenta: sparePart.get('valorDeVenta')?.value
    }));
    return collectLowSaleRatioViolations(candidates);
  }

  private submitOrderPayload(processedData: any): void {
    this.http.post(`${environment.apiEndpoint}/order`, processedData).subscribe(response => {
      this.toastService.showSuccess('Orden creada correctamente.');
      this.router.navigate(['/home']);
      this.orderSending = false;
    }, error => {
      this.orderSending = false;
      console.error('❌ Form submission error:', error);
      console.error('Error status:', error.status);
      console.error('Error statusText:', error.statusText);
      console.error('Error url:', error.url);

      if (error.status === 409) {
        const msg = error.error && typeof error.error === 'string' ? error.error : 'El número de orden ya existe. Elija otro número.';
        this.toastService.showWarning(msg);
        return;
      }

      if (error.error) {
        console.error('Error body:', error.error);
        console.error('Error body type:', typeof error.error);
        console.error('Error body keys:', error.error ? Object.keys(error.error) : 'No keys');
        console.error('Error body JSON:', JSON.stringify(error.error, null, 2));

        // Si el error es un string, mostrarlo directamente
        if (typeof error.error === 'string') {
          console.error('String error:', error.error);
          this.toastService.showError('Error al crear la orden: ' + error.error);
        }
        // Si es un objeto, intentar extraer el mensaje
        else if (error.error && error.error.message) {
          console.error('Object error message:', error.error.message);
          this.toastService.showError('Error al crear la orden: ' + error.error.message);
        }
        // Si es un error de validación, mostrar los detalles
        else if (error.error && error.error.errors) {
          console.error('Validation errors:', error.error.errors);
          console.error('Full error object:', error.error);

          // Mostrar cada error de validación individualmente
          Object.keys(error.error.errors).forEach(field => {
            console.error(`Field ${field} errors:`, error.error.errors[field]);
          });

          this.toastService.showWarning('Errores de validación. Revisa la consola para más detalles.');
        }
        else {
          console.error('Unknown error format:', error.error);
          this.toastService.showError('Error desconocido al crear la orden. Revisa la consola.');
        }
      } else {
        console.error('No error body received');
        this.toastService.showError('Error de conexión al crear la orden.');
      }
    });
  }

  validateOrder(): boolean {
    let isValid = true;
    const orderProvider = this.normalizeProviderName(this.sparePartsForm.get('providerName')?.value || '');

    if (orderProvider && !this.isProviderInCatalog(orderProvider)) {
      this.toastService.showWarning('| NOMBRE DEL PROVEEDOR | debe existir en el catálogo');
      return false;
    }

    //nombre del cliente
    if (this.sparePartsForm.get('clientName')?.value === '') {
      console.error('❌ Validación falló: clientName está vacío');
      this.toastService.showWarning('| NOMBRE DEL CLIENTE | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    //Nombre del empleado
    if (this.sparePartsForm.get('employeeName')?.value === '') {
      console.error('❌ Validación falló: employeeName está vacío');
      this.toastService.showWarning('| NOMBRE DEL EMPLEADO | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    //Siniestro
    if (this.sparePartsForm.get('siniestro')?.value === '') {
      this.toastService.showWarning('| SINIESTRO | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    //Dirección de entrega
    if (this.sparePartsForm.get('deliveryAddress')?.value === '') {
      this.toastService.showWarning('| DIRECCIÓN DE ENTREGA | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    //Es exportación
    if (this.sparePartsForm.get('isExport')?.value === '') {
      this.toastService.showWarning('| ES EXPORTACIÓN | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    //Numero de orden
    if (this.sparePartsForm.get('orderNumber')?.value === '') {
      this.toastService.showWarning('| NÚMERO DE ORDEN | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    //Enviar si o no
    if (this.sparePartsForm.get('sendYesOrNo')?.value === '') {
      this.toastService.showWarning('| ENVIAR SI O NO | no puede estar vacío');
      isValid = false;
      return isValid;
    }
    return isValid;
  }

  validateSpareParts(): boolean {
    let isValid = true;

    this.spareParts.controls.forEach((sparePart, index) => {
      const amountControl = sparePart.get('amount');
      if (amountControl && amountControl.value <= 0) {
        amountControl.setErrors({ invalidAmount: true });
        this.toastService.showWarning(`Error en repuesto ${index + 1}: La cantidad debe ser mayor a 0`);
        isValid = false;
        return isValid;
      }
      //Nombre del repuesto
      if (sparePart.get('sparePartName')?.value === '') {
        this.toastService.showWarning(`Repuesto ${index + 1}: | NOMBRE DEL REPUESTO | no puede estar vacío`);
        isValid = false;
        return isValid;
      }
      //Código del producto
      if (sparePart.get('productCode')?.value === '') {
        this.toastService.showWarning(`Repuesto ${index + 1}: | CÓDIGO DEL PRODUCTO | no puede estar vacío`);
        isValid = false;
        return isValid;
      }

      const orderProvider = (this.sparePartsForm.get('providerName')?.value || '').trim();
      const sparePartProvider = (sparePart.get('providerName')?.value || '').trim();
      if (!orderProvider && !sparePartProvider) {
        this.toastService.showWarning(`Repuesto ${index + 1}: debe tener proveedor si la orden no tiene proveedor global`);
        isValid = false;
        return isValid;
      }

      const effectiveProvider = this.normalizeProviderName(sparePartProvider || orderProvider);
      if (effectiveProvider && !this.isProviderInCatalog(effectiveProvider)) {
        this.toastService.showWarning(`Repuesto ${index + 1}: el proveedor debe existir en el catálogo`);
        isValid = false;
        return isValid;
      }
      //Estado del producto
      if (sparePart.get('isDamaged')?.value === '') {
        this.toastService.showWarning(`Repuesto ${index + 1}: | ESTADO DEL PRODUCTO | no puede estar vacío`);
        isValid = false;
        return isValid;
      }

      const saleValueError = getSaleValueValidationError(
        sparePart.get('valorDeVenta')?.value,
        sparePart.get('sparePartName')?.value || `#${index + 1}`
      );
      if (saleValueError) {
        this.toastService.showWarning(`Repuesto ${index + 1}: ${saleValueError}`);
        isValid = false;
        return isValid;
      }

      return isValid;
    });

    return isValid;
  }

  // Función auxiliar para convertir camelCase a PascalCase
  toPascalCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Función para convertir todas las propiedades de un objeto a PascalCase
  convertToPascalCase(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.convertToPascalCase(item));
    }

    const converted: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const pascalKey = this.toPascalCase(key);
        converted[pascalKey] = this.convertToPascalCase(obj[key]);
      }
    }
    return converted;
  }

  processFormDataForBackend(formData: any): any {
    // Crear una copia profunda de los datos para no modificar el original
    const processedData = JSON.parse(JSON.stringify(formData));

    const orderDateStr = (processedData.orderDate as string) || '';
    const orderBase =
      orderDateStr.length >= 8
        ? new Date(`${orderDateStr}T12:00:00`)
        : new Date();
    processedData.creationDate = orderBase.toISOString();

    // Procesar repuestos
    if (processedData.spareParts && Array.isArray(processedData.spareParts)) {
      processedData.spareParts.forEach((sparePart: any, index: number) => {
        // Convertir valores numéricos del formato string al formato numérico
        sparePart.unitPrice = this.normalizeNumericValue(sparePart.unitPrice || '0');
        sparePart.amount = this.normalizeNumericValue(sparePart.amount || '1');
        sparePart.valorDeVenta = this.normalizeNumericValue(sparePart.valorDeVenta ?? '');
        sparePart.netWheight = this.normalizeNumericValue(sparePart.netWheight || '0');
        sparePart.grossWheight = this.normalizeNumericValue(sparePart.grossWheight || '0');

        // Eliminar campos que no existen en el modelo SparePartModel del backend
        delete sparePart.totalTax; // Este campo no existe en el modelo
        delete sparePart.productState; // Este campo no existe en el modelo

        // Convertir valores según el tipo esperado por el backend
        // IsDamaged es string en el backend (DTO), NeedReview es boolean en el backend (DTO)
        sparePart.needReview = sparePart.needReview === true || sparePart.needReview === 'true';
        sparePart.isDamaged = normalizeIsDamaged(sparePart.isDamaged);

        // Asegurar que los campos de texto no sean null
        sparePart.sparePartName = sparePart.sparePartName || '';
        sparePart.sparePartNameEs = sparePart.sparePartNameEs || '';
        sparePart.sparePartNamePt = sparePart.sparePartNamePt || '';
        sparePart.productCode = sparePart.productCode || '';
        sparePart.providerName = (sparePart.providerName || processedData.providerName || '').trim();
        sparePart.taxNote = sparePart.taxNote || '';
        sparePart.ncm = sparePart.ncm || '';
        sparePart.comments = sparePart.comments || '';
        sparePart.emergentComment = sparePart.emergentComment || '';
        sparePart.shippingReceipt = sparePart.shippingReceipt || '';
        sparePart.isFant = !!sparePart.isFant;
        sparePart.fantUrgency = defaultFantUrgency(sparePart.isFant, sparePart.fantUrgency);
        sparePart.recompra = !!sparePart.recompra;
        sparePart.control = !!sparePart.control;
        sparePart.codBr = sparePart.codBr || '';
        sparePart.country = sparePart.country || '';

        // Calcular fecha de llegada esperada
        if (sparePart.arrivalDays && parseInt(sparePart.arrivalDays) > 0) {
          const expectedDate = new Date(orderBase);
          expectedDate.setDate(expectedDate.getDate() + parseInt(sparePart.arrivalDays));
          sparePart.expectedArrivalDate = expectedDate.toISOString();
        } else {
          sparePart.expectedArrivalDate = orderBase.toISOString();
        }

        // Eliminar campos que no deben enviarse al backend
        delete sparePart.arrivalDays;
      });
    }

    // Procesar campos de la orden
    processedData.providerName = processedData.providerName || '';
    processedData.providerPhone = processedData.providerPhone || '';
    processedData.clientName = processedData.clientName || '';
    processedData.clientPhone = processedData.clientPhone || '';
    processedData.invoiceNumber = processedData.invoiceNumber || '';
    processedData.employeeName = processedData.employeeName || '';
    processedData.siniestro = processedData.siniestro || '';
    processedData.orderNumber = processedData.orderNumber || '';
    processedData.deliveryAddress = processedData.deliveryAddress || '';
    processedData.carMakeModel = processedData.carMakeModel || '';
    processedData.isExport = processedData.isExport || 'no';
    processedData.sendYesOrNo = processedData.sendYesOrNo || 'false';
    processedData.clienteUY = processedData.clienteUY || 'AUTOCOMERCIAL';
    processedData.emergentComment = processedData.emergentComment || '';

    // Procesar WorkshopId - convertir string vacío o "null" a null para nullable int
    if (processedData.workshopId === '' || processedData.workshopId === 'null' || processedData.workshopId === null || processedData.workshopId === undefined) {
      processedData.workshopId = null;
    } else {
      // Convertir a número si es un string numérico
      const workshopIdNum = parseInt(processedData.workshopId);
      processedData.workshopId = isNaN(workshopIdNum) ? null : workshopIdNum;
    }

    // Snapshot de taller: solo se envía cuando el usuario eligió edición "solo esta orden".
    processedData.workshopName = this.normalizeWorkshopField(processedData.workshopName);
    processedData.workshopAddress = this.normalizeWorkshopField(processedData.workshopAddress);
    processedData.workshopPhone = this.normalizeWorkshopField(processedData.workshopPhone);
    processedData.workshopCity = this.normalizeWorkshopField(processedData.workshopCity);
    processedData.workshopDepartment = this.normalizeWorkshopField(processedData.workshopDepartment);

    if (!processedData.workshopId) {
      processedData.workshopName = null;
      processedData.workshopAddress = null;
      processedData.workshopPhone = null;
      processedData.workshopCity = null;
      processedData.workshopDepartment = null;
    }

    // Eliminar campos que no deberían enviarse al backend
    delete processedData.stateOrder; // Este campo no existe en el modelo OrderModel
    delete processedData.orderDate; // Solo sirvió para derivar creationDate

    // Asegurar formato correcto para campos de tipo string
    if (typeof processedData.sendYesOrNo === 'boolean') {
      processedData.sendYesOrNo = processedData.sendYesOrNo ? 'true' : 'false';
    }

    // Convertir a PascalCase para que coincida con el modelo .NET
    const pascalCaseData = this.convertToPascalCase(processedData);

    // Envolver los datos en la estructura esperada por el backend
    // El backend espera: { orderModel: { ...datos de la orden... } }
    return {
      orderModel: pascalCaseData
    };
  }

  autoFillForm(): void {
    // Auto-completar campos de la orden
    this.sparePartsForm.patchValue({
      orderDate: this.todayLocalDateString(),
      providerName: 'Proveedor de Prueba S.A.',
      providerPhone: '099123456',
      clientName: 'Cliente de Prueba',
      clientPhone: '098765432',
      invoiceNumber: 'INV-TEST-001',
      employeeName: 'Carlos Rodríguez', // Debe ser uno de los nombres válidos
      siniestro: 'SIN-TEST-001',
      orderNumber: 'ORD-TEST-' + Date.now(),
      deliveryAddress: 'Dirección de Prueba 123',
      carMakeModel: 'Toyota Corolla 2020',
      isExport: 'no',
      sendYesOrNo: true,
      clienteUY: 'AUTOCOMERCIAL',
      emergentComment: 'Verificar prioridad de esta orden'
    });

    // Limpiar repuestos existentes y agregar uno nuevo con datos de prueba
    while (this.spareParts.length > 0) {
      this.spareParts.removeAt(0);
    }

    this.addSparePart();

    // Auto-completar el primer repuesto
    if (this.spareParts.length > 0) {
      const sparePart = this.spareParts.at(0);
      sparePart.patchValue({
        sparePartName: 'Filtro de Aceite',
        sparePartNameEs: 'Filtro de Aceite',
        sparePartNamePt: 'Filtro de Óleo',
        unitPrice: '25,50',
        amount: '2',
        taxNote: 'NF-123456',
        ncm: '84212300',
        productCode: 'FLT-OIL-001',
        providerName: 'Proveedor de Prueba S.A.',
        comments: 'Repuesto de prueba para testing',
        emergentComment: 'Pieza delicada: revisar antes de manipular',
        shippingReceipt: 'REC-001',
        isFant: false,
        fantUrgency: null,
        recompra: false,
        control: false,
        isDamaged: SPARE_PART_DAMAGED_NO,
        codBr: 'BR-001',
        valorDeVenta: '30,00',
        arrivalDays: '15',
        netWheight: '0,5',
        grossWheight: '0,7',
        country: 'Brasil'
      });
    }

    // Forzar actualización de la validación
    this.sparePartsForm.updateValueAndValidity();
    setTimeout(() => {
      this.sparePartsForm.updateValueAndValidity();
    }, 100);

    this.toastService.showInfo('Formulario auto-completado con datos de prueba.');
  }

  // Métodos para manejar la creación de nuevos talleres
  onWorkshopChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const value = selectElement.value;

    if (value === 'add-new') {
      this.openCreateWorkshopModal();
    }
  }

  // Métodos para manejar la información del taller
  openWorkshopInfo(workshopId: number): void {
    const workshop = this.workshops.find(w => w.workshopId === workshopId);
    if (workshop) {
      const selectedWorkshopId = this.sparePartsForm.get('workshopId')?.value;
      const hasSnapshot = !!this.sparePartsForm.get('workshopName')?.value;
      const isSelectedWorkshop =
        selectedWorkshopId !== null &&
        selectedWorkshopId !== undefined &&
        selectedWorkshopId.toString() === workshopId.toString();

      if (isSelectedWorkshop && hasSnapshot) {
        this.selectedWorkshopInfo = {
          ...workshop,
          name: this.sparePartsForm.get('workshopName')?.value,
          address: this.sparePartsForm.get('workshopAddress')?.value,
          phoneNumber: this.sparePartsForm.get('workshopPhone')?.value,
          city: this.sparePartsForm.get('workshopCity')?.value,
          department: this.sparePartsForm.get('workshopDepartment')?.value
        };
      } else {
        this.selectedWorkshopInfo = workshop;
      }

      this.showWorkshopInfoModal = true;
    }
  }

  closeWorkshopInfo(): void {
    this.showWorkshopInfoModal = false;
  }

  // Métodos para el dropdown personalizado de talleres
  toggleWorkshopDropdown(): void {
    this.showWorkshopDropdown = !this.showWorkshopDropdown;
  }

  selectWorkshop(value: string): void {
    this.showWorkshopDropdown = false;

    if (value === 'add-new') {
      this.openCreateWorkshopModal();
      return;
    }

    if (value === 'null') {
      this.sparePartsForm.patchValue({ workshopId: null });
      this.clearWorkshopSnapshotFields();
    } else {
      this.sparePartsForm.patchValue({ workshopId: value });
      this.clearWorkshopSnapshotFields();
    }
  }

  getSelectedWorkshopDisplay(): string {
    const workshopId = this.sparePartsForm.get('workshopId')?.value;
    const workshopNameSnapshot = this.sparePartsForm.get('workshopName')?.value;
    const workshopCitySnapshot = this.sparePartsForm.get('workshopCity')?.value;
    const workshopAddressSnapshot = this.sparePartsForm.get('workshopAddress')?.value;

    if (!workshopId || workshopId === 'null') {
      return 'Sin asignar';
    }

    if (workshopNameSnapshot) {
      let display = workshopNameSnapshot;
      if (workshopCitySnapshot) {
        display += ` (${workshopCitySnapshot})`;
      }
      if (workshopAddressSnapshot) {
        display += ` - ${workshopAddressSnapshot}`;
      }
      return display;
    }

    const workshop = this.workshops.find(w => w.workshopId.toString() === workshopId.toString());
    if (workshop) {
      let display = workshop.name;
      if (workshop.city) {
        display += ` (${workshop.city})`;
      }
      if (workshop.address) {
        display += ` - ${workshop.address}`;
      }
      return display;
    }

    return 'Seleccionar taller';
  }

  // Cerrar dropdown al hacer clic fuera
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.workshop-dropdown-container')) {
      this.showWorkshopDropdown = false;
    }
  }

  openManageProvidersModal(): void {
    this.showManageProvidersModal = true;
  }

  closeManageProvidersModal(): void {
    this.showManageProvidersModal = false;
    this.creatingProvider = false;
    this.deletingProviderId = null;
    this.manageProviderForm.reset({ name: '' });
  }

  createProvider(): void {
    if (this.manageProviderForm.invalid) {
      this.manageProviderForm.markAllAsTouched();
      return;
    }

    const rawName = this.manageProviderForm.get('name')?.value || '';
    const name = this.normalizeProviderName(rawName);
    if (!name) {
      this.toastService.showWarning('El nombre del proveedor no puede quedar vacío.');
      return;
    }

    this.creatingProvider = true;
    this.providersService.createProvider({ name }).subscribe({
      next: (provider) => {
        const idx = this.providers.findIndex(p => p.providerId === provider.providerId);
        if (idx >= 0) {
          this.providers[idx] = provider;
        } else {
          this.providers.push(provider);
        }

        this.providers = this.providers.sort((a, b) => a.name.localeCompare(b.name));
        this.sparePartsForm.patchValue({ providerName: provider.name });
        this.manageProviderForm.reset({ name: '' });
        this.toastService.showSuccess(`Proveedor "${provider.name}" guardado correctamente.`);
      },
      error: (error) => {
        this.creatingProvider = false;
        console.error('Error al crear proveedor:', error);
        if (error?.status === 409) {
          this.toastService.showWarning('Ese proveedor ya existe.');
          return;
        }
        this.toastService.showError('No se pudo guardar el proveedor. Intenta nuevamente.');
      },
      complete: () => {
        this.creatingProvider = false;
      }
    });
  }

  deleteProvider(provider: ProviderModel): void {
    if (!provider?.providerId) {
      return;
    }

    const confirmDelete = confirm(`¿Quitar el proveedor "${provider.name}" del selector?`);
    if (!confirmDelete) {
      return;
    }

    this.deletingProviderId = provider.providerId;
    this.providersService.deleteProvider(provider.providerId).subscribe({
      next: () => {
        this.providers = this.providers.filter(p => p.providerId !== provider.providerId);
        this.toastService.showSuccess(`Proveedor "${provider.name}" quitado del selector.`);
      },
      error: (error) => {
        this.deletingProviderId = null;
        console.error('Error al eliminar proveedor:', error);
        this.toastService.showError('No se pudo quitar el proveedor. Intenta nuevamente.');
      },
      complete: () => {
        this.deletingProviderId = null;
      }
    });
  }

  private normalizeProviderName(value: string): string {
    return value
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .join(' ');
  }

  private normalizeProviderKey(value: string): string {
    return this.normalizeProviderName(value).toUpperCase();
  }

  private isProviderInCatalog(providerName: string): boolean {
    const normalizedCandidate = this.normalizeProviderKey(providerName);
    if (!normalizedCandidate) {
      return false;
    }

    return this.providers.some(provider =>
      this.normalizeProviderKey(provider.name || '') === normalizedCandidate
    );
  }

  openCreateWorkshopModal(): void {
    this.showCreateWorkshopModal = true;
    this.sparePartsForm.patchValue({ workshopId: 'null' });
    this.clearWorkshopSnapshotFields();
  }

  createWorkshop(): void {
    if (this.createWorkshopForm.valid) {
      this.creatingWorkshop = true;

      const workshopData = this.createWorkshopForm.value;

      this.workshopsService.createWorkshop(workshopData).subscribe({
        next: (newWorkshop) => {
          // Agregar el nuevo taller a la lista
          this.workshops.push(newWorkshop);

          // Seleccionar automáticamente el nuevo taller
          this.sparePartsForm.patchValue({ workshopId: newWorkshop.workshopId });
          this.clearWorkshopSnapshotFields();

          // Cerrar el modal y resetear el formulario
          this.cancelCreateWorkshop();

          this.toastService.showSuccess(`Taller "${newWorkshop.name}" creado y seleccionado.`);
        },
        error: (error) => {
          console.error('Error al crear taller:', error);
          this.toastService.showError('No se pudo crear el taller. Intenta nuevamente.');
        },
        complete: () => {
          this.creatingWorkshop = false;
        }
      });
    }
  }

  cancelCreateWorkshop(): void {
    this.showCreateWorkshopModal = false;
    this.createWorkshopForm.reset({
      name: '',
      address: '',
      phoneNumber: '',
      city: '',
      department: '',
      company: 'AUTOCOMERCIAL'
    });
  }

  openEditWorkshopModal(): void {
    if (!this.selectedWorkshopInfo) {
      return;
    }

    this.showWorkshopInfoModal = false;
    this.workshopEditScope = 'order-only';
    this.editWorkshopForm.reset({
      name: this.selectedWorkshopInfo.name || '',
      address: this.selectedWorkshopInfo.address || '',
      phoneNumber: this.selectedWorkshopInfo.phoneNumber || '',
      city: this.selectedWorkshopInfo.city || '',
      department: this.selectedWorkshopInfo.department || '',
      company: this.selectedWorkshopInfo.company || 'AUTOCOMERCIAL'
    });

    this.showEditWorkshopModal = true;
  }

  closeEditWorkshopModal(): void {
    this.showEditWorkshopModal = false;
    this.updatingWorkshop = false;
  }

  saveWorkshopEdit(): void {
    if (!this.selectedWorkshopInfo || !this.selectedWorkshopInfo.workshopId) {
      this.toastService.showWarning('No se encontró el taller a editar.');
      return;
    }

    if (this.editWorkshopForm.invalid) {
      this.editWorkshopForm.markAllAsTouched();
      return;
    }

    const workshopId = this.selectedWorkshopInfo.workshopId;
    const payload: WorkshopModel = {
      workshopId,
      name: this.normalizeWorkshopField(this.editWorkshopForm.value.name) || '',
      address: this.normalizeWorkshopField(this.editWorkshopForm.value.address) || undefined,
      phoneNumber: this.normalizeWorkshopField(this.editWorkshopForm.value.phoneNumber) || undefined,
      city: this.normalizeWorkshopField(this.editWorkshopForm.value.city) || undefined,
      department: this.normalizeWorkshopField(this.editWorkshopForm.value.department) || undefined,
      company: this.normalizeWorkshopField(this.editWorkshopForm.value.company) || 'AUTOCOMERCIAL'
    };

    if (!payload.name) {
      this.toastService.showWarning('El nombre del taller es obligatorio.');
      return;
    }

    if (this.workshopEditScope === 'global') {
      this.updatingWorkshop = true;
      this.workshopsService.updateWorkshop(workshopId, payload).subscribe({
        next: (updatedWorkshop) => {
          this.replaceWorkshopInList(updatedWorkshop);
          this.selectedWorkshopInfo = updatedWorkshop;
          this.clearWorkshopSnapshotFields();
          this.closeEditWorkshopModal();
          this.closeWorkshopInfo();
          this.toastService.showSuccess('Taller actualizado permanentemente en la base de datos.');
        },
        error: (error) => {
          console.error('Error al actualizar taller global:', error);
          this.toastService.showError('No se pudo actualizar el taller en la base de datos.');
        },
        complete: () => {
          this.updatingWorkshop = false;
        }
      });
      return;
    }

    this.applyWorkshopSnapshotToOrder(payload, workshopId);
    this.selectedWorkshopInfo = {
      ...this.selectedWorkshopInfo,
      ...payload
    };
    this.closeEditWorkshopModal();
    this.closeWorkshopInfo();
    this.toastService.showSuccess('Cambios aplicados solo para esta orden.');
  }

  private applyWorkshopSnapshotToOrder(workshop: WorkshopModel, workshopId: number): void {
    this.sparePartsForm.patchValue({
      workshopId: workshopId,
      workshopName: this.normalizeWorkshopField(workshop.name),
      workshopAddress: this.normalizeWorkshopField(workshop.address),
      workshopPhone: this.normalizeWorkshopField(workshop.phoneNumber),
      workshopCity: this.normalizeWorkshopField(workshop.city),
      workshopDepartment: this.normalizeWorkshopField(workshop.department)
    });
  }

  private clearWorkshopSnapshotFields(): void {
    this.sparePartsForm.patchValue({
      workshopName: null,
      workshopAddress: null,
      workshopPhone: null,
      workshopCity: null,
      workshopDepartment: null
    });
  }

  private normalizeWorkshopField(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const parsed = String(value).trim();
    return parsed === '' ? null : parsed;
  }

  private replaceWorkshopInList(updatedWorkshop: WorkshopModel): void {
    const idx = this.workshops.findIndex(w => w.workshopId === updatedWorkshop.workshopId);
    if (idx === -1) {
      this.workshops.push(updatedWorkshop);
      return;
    }

    this.workshops[idx] = updatedWorkshop;
  }

  goBack(): void {
    this.router.navigate(['/']);
  }
}
